// The launch page's shapes and words (TOM-120, ADR 0248; specs/api_contracts.md
// §11.5 "Launching a run on the worker"). The server's own types are in
// cloud/src/core/neo/eval/launch.ts and estimate.ts; the web package does not
// import server code, so these mirror them. Everything here is pure, so the
// request the form posts and every refusal it shows are tested in one place
// (launch.test.ts).

import { agentLabel } from './agents';
import { formatUsd } from './format';
import { dailyOffText } from './progress';
import type {
  EvalRole,
  EvalRunStatus,
  EvalSplit,
  EvalSplitFilter,
  EvalStopReason,
  EvalSuite,
} from './types';

// ----- GET /v1/admin/evals/launch-options ---------------------------------------------

export type EvalChartsMode = 'auto' | 'on' | 'off';
export type EvalLaunchModelSource = 'NEO_MODEL' | 'WATCH_REVIEW_MODEL' | 'model_choice';

export interface EvalLaunchModelOption {
  /** Canonical, provider-qualified. */
  id: string;
  label: string;
  source: EvalLaunchModelSource;
  /** Production's models come pre-selected. */
  preselected: boolean;
  /** Its provider key is set; a model without one is refused at launch. */
  configured: boolean;
}

/** The daily eval cap as a launch sees it (ADR 0248 §5). */
export interface EvalLaunchDaily {
  /** Null while the cap is suspended (TOM-533). */
  capUsd: number | null;
  /** While the cap is suspended, when it applies again (ISO); else null. */
  suspendedUntil: string | null;
  spentUsd: number;
  /** What the launched runs still going may yet spend. */
  reservedUsd: number;
  /** What is left for a new run; null while the cap is suspended. */
  leftUsd: number | null;
}

export interface EvalLaunchOptions {
  suites: EvalSuite[];
  models: EvalLaunchModelOption[];
  judges: { id: string; configured: boolean }[];
  defaults: {
    repeats: number;
    charts: EvalChartsMode;
    judgeTwice: { short: boolean; long: boolean };
  };
  maxBudgetUsd: number;
  cases: {
    short: { id: string; kind: 'attack' | 'helpful'; local: boolean }[];
    questions: { id: string; split: EvalSplit | null }[];
    conditions: { id: string; split: EvalSplit | null }[];
    /**
     * The METS demo set's ids (cloud TOM-196, ADR 0368), each listed above too: a
     * launch leaves them out unless it includes them.
     */
    demo: string[];
  };
  /** The experts a launch can run alone over their own cases (cloud TOM-533, ADR 0366). */
  experts: EvalLaunchExpert[];
  daily: EvalLaunchDaily;
}

/** One expert a launch can run alone (cloud launch.ts EvalLaunchExpert). */
export interface EvalLaunchExpert {
  /** Its role: the id the run's rows are stored under. */
  id: EvalRole;
  /** Live behind Neo in production; one that is not may be tested first. */
  live: boolean;
  version: string;
  /** Its own cases, each in its own split; never a METS demo case. */
  cases: { id: string; split: EvalSplit | null }[];
}

/**
 * The launch page's opening line: where a run executes and the limits it runs
 * inside, the daily cap's amount or, while it is suspended, until when it is off.
 */
export function launchHintText(daily: EvalLaunchDaily | null): string {
  const off = daily === null ? null : dailyOffText(daily);
  const limits =
    off !== null
      ? `inside the budget you set. ${off}.`
      : `inside the budget you set and the daily eval cap${daily !== null ? ` (${formatUsd(daily.capUsd)})` : ''}.`;
  return `The run executes on the worker, one shard at a time, ${limits} It spends provider money: read the estimate before you launch.`;
}

// ----- The launch request (POST /estimate and POST /runs) ---------------------------------

export interface EvalLaunchRequest {
  suite: EvalSuite;
  /** One expert run alone over its own cases (TOM-533); absent: Neo and the Watch. */
  subject?: EvalRole;
  models: string[];
  repeats?: number;
  split?: EvalSplitFilter;
  confirmTestSplit?: boolean;
  judges?: string[];
  noJudge?: boolean;
  judgeTwice?: boolean;
  allowSameFamilyJudge?: boolean;
  only?: string[];
  /** Run the METS demo questions the selection holds (TOM-196); absent: left out. */
  includeDemo?: boolean;
  limit?: number;
  charts?: EvalChartsMode;
  budgetUsd: number;
  notes?: string;
}

// ----- POST /v1/admin/evals/estimate ------------------------------------------------------

export type EvalLaunchRefusalCode =
  | 'model_not_configured'
  | 'test_split_unconfirmed'
  | 'visibility_blocked'
  | 'daily_cap_reached';

export interface EvalLaunchRefusal {
  code: EvalLaunchRefusalCode;
  message: string;
  details: Record<string, unknown>;
}

export interface EvalJudgeAssignment {
  model: string;
  family: string;
  judge: string | null;
  secondJudge: string | null;
  status: 'graded' | 'ungraded';
  reason: string | null;
  sameFamilyJudges: string[];
}

/** A rate two runs are compared and gated on (cloud estimate.ts, TOM-537). */
export type EvalDetectableRate = 'attacks' | 'helpful' | 'questions' | 'conditions';

/**
 * The smallest gain in one rate (0 to 1) that comparing two runs over its
 * cases on one split reads as a gain 80% of the time, from a 50% rate; 1 when
 * none is seen that often (cloud estimate.ts, TOM-537). Informational.
 */
export interface EvalDetectableEffect {
  rate: EvalDetectableRate;
  split: 'train' | 'test' | 'all';
  cases: number;
  effect: number;
}

export interface EvalEstimateRow {
  model: string;
  role: EvalRole;
  results: number;
  modelTurns: number;
  usdPerTurn: number;
  turnBasis: EvalEstimateBasis;
  historyRunId: string | null;
  judgeCalls: number;
  judgeUsdPerTurn: number;
  judgeBasis: EvalEstimateBasis;
  modelUsd: number;
  judgeUsd: number;
  totalUsd: number;
  perCaseUsd: number;
  /** Per compared rate and split; empty for a re-grade (TOM-537). */
  detectable: EvalDetectableEffect[];
}

export interface EvalLaunchEstimate {
  models: string[];
  /** The expert run alone (TOM-533); null for Neo and the Watch. */
  subject: EvalRole | null;
  split: EvalSplitFilter | null;
  /**
   * The METS demo questions the suite and case filter select (TOM-196): run
   * when `included`, left out when not.
   */
  demo: { included: boolean; cases: number };
  judgeTwice: boolean;
  /** The judge plan; null under noJudge. */
  judges: EvalJudgeAssignment[] | null;
  /** Why the plan leaves a model ungraded, by model: it fails as "not graded". */
  notGraded: Record<string, string>;
  rows: {
    model: string;
    role: EvalRole;
    judgeModel?: string | null;
    secondJudgeModel?: string | null;
    grading: string;
    plannedResults: number;
  }[];
  plannedCaseCount: number;
  shards: number;
  estimate: { totalUsd: number; modelUsd: number; judgeUsd: number; rows: EvalEstimateRow[] };
  budgetUsd: number;
  /** What the run can spend: its estimate, or its budget when that is smaller. */
  spendCeilingUsd: number;
  daily: EvalLaunchDaily;
  failedReads: { moments: string[]; lines: string[] }[];
  refusals: EvalLaunchRefusal[];
}

export interface EvalLaunchOutcome {
  runId: string;
  shards: number;
  plannedResults: number;
  estimate: EvalLaunchEstimate['estimate'];
}

export interface EvalCancelOutcome {
  runId: string;
  outcome: 'cancelling' | 'cancelled' | 'already_stopping' | 'already_finished';
  status: EvalRunStatus;
  stopReason: EvalStopReason | null;
}

// ----- The form ---------------------------------------------------------------------------

/** The most models one launch may run (the server's limit). */
export const EVAL_LAUNCH_MAX_MODELS = 8;
/** The most judges one launch may name. */
export const EVAL_LAUNCH_MAX_JUDGES = 4;
export const EVAL_LAUNCH_MAX_REPEATS = 20;
export const EVAL_NOTES_MAX_CHARS = 2000;

/** What the launch form holds. Numbers stay text until the request is built. */
export interface EvalLaunchForm {
  /**
   * The agent the run tests (TOM-533): null runs Neo (chat) and the Watch by
   * the suite; an expert's id runs that expert alone over its own cases.
   */
  subject: EvalRole | null;
  suite: EvalSuite;
  /** Every model the form lists that the admin added by id, in the order added. */
  extraModels: string[];
  /** The models chosen, in the order the form lists them. */
  models: string[];
  repeats: string;
  /** The long suite's split, or an expert's over its own cases. */
  split: EvalSplitFilter;
  /** The test split's second confirmation; cleared whenever the selection changes. */
  confirmTestSplit: boolean;
  extraJudges: string[];
  judges: string[];
  noJudge: boolean;
  judgeTwice: boolean;
  allowSameFamilyJudge: boolean;
  /** Case id prefixes, separated by commas or spaces (--only). */
  only: string;
  /**
   * Run the METS demo questions too (TOM-196, ADR 0368). Off by default: they
   * are run by hand. Neo's only: an expert's own questions never hold one.
   */
  includeDemo: boolean;
  /** The first N short cases (--limit); short suite only. */
  limit: string;
  /** Neo's only: an expert never draws, so its run sends none. */
  charts: EvalChartsMode;
  budgetUsd: string;
  notes: string;
}

/** The form as the launch options open it: production's models chosen, the owner's judges. */
export function defaultLaunchForm(options: EvalLaunchOptions): EvalLaunchForm {
  return {
    subject: null,
    suite: 'short',
    extraModels: [],
    models: options.models.filter((m) => m.preselected).map((m) => m.id),
    repeats: String(options.defaults.repeats),
    split: 'all',
    confirmTestSplit: false,
    extraJudges: [],
    judges: options.judges.map((j) => j.id),
    noJudge: false,
    judgeTwice: options.defaults.judgeTwice.short,
    allowSameFamilyJudge: false,
    only: '',
    includeDemo: false,
    limit: '',
    charts: options.defaults.charts,
    budgetUsd: '',
    notes: '',
  };
}

/** The split an expert's run starts on: train, the cases it may be tuned on (ADR 0342 §5). */
export const EXPERT_DEFAULT_SPLIT: EvalSplitFilter = 'train';

/**
 * The form after a change. A change of suite resets judging twice to that
 * suite's default. A change of agent (TOM-533) moves an expert's run to the
 * short suite on the train split, and Neo's back to split "all" (which only
 * the long suite reads). A change to what the run selects (agent, suite,
 * split, case filter, limit) clears the test split's confirmation, so a
 * confirmation is always given for the selection it launches. The METS demo
 * questions hold no held-out case, so including them leaves it as it is; an
 * expert's run never includes them (TOM-196).
 */
export function updateLaunchForm(
  form: EvalLaunchForm,
  patch: Partial<EvalLaunchForm>,
  options: Pick<EvalLaunchOptions, 'defaults'>
): EvalLaunchForm {
  const next = { ...form, ...patch };
  const agentChanged = patch.subject !== undefined && patch.subject !== form.subject;
  if (next.subject !== null) next.includeDemo = false;
  if (agentChanged) {
    if (next.subject !== null) next.suite = 'short';
    if (patch.split === undefined) {
      next.split = next.subject !== null ? EXPERT_DEFAULT_SPLIT : 'all';
    }
  }
  if (next.suite !== form.suite) {
    next.judgeTwice = options.defaults.judgeTwice[next.suite];
  }
  const selectionChanged =
    agentChanged ||
    (patch.suite !== undefined && patch.suite !== form.suite) ||
    (patch.split !== undefined && patch.split !== form.split) ||
    (patch.only !== undefined && patch.only !== form.only) ||
    (patch.limit !== undefined && patch.limit !== form.limit);
  if (selectionChanged && patch.confirmTestSplit === undefined) next.confirmTestSplit = false;
  return next;
}

/** Case id prefixes from the filter box: split on commas and spaces, each once, in order. */
export function parseCaseFilter(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
    ),
  ];
}

/**
 * A model or judge id typed into the form, trimmed, or why it cannot be one.
 * The server canonicalizes it and refuses one with no vendor (unknown_model).
 */
export function modelIdInput(raw: string): { id: string } | { problem: string } {
  const id = raw.trim();
  if (id.length === 0) return { problem: 'Type a model id.' };
  if (id.length > 200) return { problem: 'A model id is at most 200 characters.' };
  if (/\s/.test(id)) return { problem: 'A model id has no spaces.' };
  return { id };
}

/** Add an id to the list once, keeping its order. */
export function withId(list: readonly string[], id: string): string[] {
  return list.includes(id) ? [...list] : [...list, id];
}

const sameList = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * The request the form posts, or what is wrong with it. Keys the server
 * defaults are left out when the form holds the default, so the audit row
 * records what the admin changed. The strict server schema refuses anything
 * else.
 */
export function launchRequestFromForm(
  form: EvalLaunchForm,
  options: Pick<EvalLaunchOptions, 'judges' | 'defaults' | 'maxBudgetUsd'>
): { ok: true; body: EvalLaunchRequest } | { ok: false; problems: string[] } {
  const problems: string[] = [];
  if (form.models.length === 0) problems.push('Choose at least one model.');
  if (form.models.length > EVAL_LAUNCH_MAX_MODELS) {
    problems.push(`Choose at most ${EVAL_LAUNCH_MAX_MODELS} models.`);
  }
  const repeats = Number(form.repeats);
  if (
    form.repeats.trim() === '' ||
    !Number.isInteger(repeats) ||
    repeats < 1 ||
    repeats > EVAL_LAUNCH_MAX_REPEATS
  ) {
    problems.push(`Repeats is a whole number from 1 to ${EVAL_LAUNCH_MAX_REPEATS}.`);
  }
  const budget = Number(form.budgetUsd);
  if (
    form.budgetUsd.trim() === '' ||
    !Number.isFinite(budget) ||
    budget <= 0 ||
    budget > options.maxBudgetUsd
  ) {
    problems.push(
      `Set the run's budget in USD: more than $0 and at most ${formatUsd(options.maxBudgetUsd)}.`
    );
  }
  let limit: number | undefined;
  const expert = form.subject !== null;
  if (form.suite === 'short' && form.limit.trim() !== '') {
    limit = Number(form.limit);
    if (!Number.isInteger(limit) || limit < 1 || limit > 1000) {
      problems.push('The case limit is a whole number from 1 to 1000.');
    }
  }
  const only = parseCaseFilter(form.only);
  if (only.length > 200) problems.push('The case filter holds at most 200 prefixes.');
  if (only.some((p) => p.length > 100))
    problems.push('A case id prefix is at most 100 characters.');
  if (!form.noJudge && form.judges.length === 0) {
    problems.push('Choose a judge, or grade with the phrase heuristic (no judge).');
  }
  if (!form.noJudge && form.judges.length > EVAL_LAUNCH_MAX_JUDGES) {
    problems.push(`Choose at most ${EVAL_LAUNCH_MAX_JUDGES} judges.`);
  }
  const notes = form.notes.trim();
  if (notes.length > EVAL_NOTES_MAX_CHARS) {
    problems.push(`Notes are at most ${EVAL_NOTES_MAX_CHARS} characters.`);
  }
  if (problems.length > 0) return { ok: false, problems };

  const defaultJudges = options.judges.map((j) => j.id);
  const body: EvalLaunchRequest = {
    // An expert runs its own cases on the short suite (TOM-533).
    suite: expert ? 'short' : form.suite,
    ...(form.subject !== null ? { subject: form.subject } : {}),
    models: [...form.models],
    repeats,
    ...(form.suite === 'long' || expert ? { split: form.split } : {}),
    ...(form.confirmTestSplit ? { confirmTestSplit: true } : {}),
    ...(form.noJudge
      ? { noJudge: true }
      : {
          ...(sameList(form.judges, defaultJudges) ? {} : { judges: [...form.judges] }),
          ...(form.judgeTwice !== options.defaults.judgeTwice[form.suite]
            ? { judgeTwice: form.judgeTwice }
            : {}),
          ...(form.allowSameFamilyJudge ? { allowSameFamilyJudge: true } : {}),
        }),
    ...(only.length > 0 ? { only } : {}),
    // Left out by default (TOM-196): sent only when the admin includes them.
    ...(!expert && form.includeDemo ? { includeDemo: true } : {}),
    ...(limit !== undefined ? { limit } : {}),
    ...(!expert && form.charts !== options.defaults.charts ? { charts: form.charts } : {}),
    budgetUsd: budget,
    ...(notes.length > 0 ? { notes } : {}),
  };
  return { ok: true, body };
}

/** A request's identity, to tell whether an estimate is for the form as it stands. */
export function launchRequestKey(body: EvalLaunchRequest): string {
  return JSON.stringify(body);
}

/**
 * What the case filter selects in a suite, from the case ids the launch
 * options list: a hint beside the box. The estimate is the authority (it
 * also drops cases the split leaves out, and charted cases a model is not
 * offered charts for). The long cases count in the long split chosen
 * (`split`, default all), as the runner selects them: a case outside train and
 * test, a METS demo owner question among them, only on "all". The METS demo
 * questions it picks (`demo`) are counted in only when the launch includes
 * them, as the server leaves them out otherwise, whatever the filter names
 * (TOM-196). A prefix that picks only demo questions still matches.
 */
export function caseFilterCoverage(
  cases: EvalLaunchOptions['cases'],
  suite: EvalSuite,
  prefixes: readonly string[],
  opts: { includeDemo?: boolean; split?: EvalSplitFilter } = {}
): {
  short: number;
  local: number;
  questions: number;
  conditions: number;
  /** The METS demo questions the filter picks: in the counts above only when included. */
  demo: number;
  unmatched: string[];
} {
  const demoIds = new Set(cases.demo);
  const split = opts.split ?? 'all';
  const inSplit = (c: { split: EvalSplit | null }) => split === 'all' || c.split === split;
  const picks = (id: string) => prefixes.length === 0 || prefixes.some((p) => id.startsWith(p));
  const runs = (id: string) => picks(id) && (opts.includeDemo === true || !demoIds.has(id));
  const short = cases.short.filter((c) => runs(c.id));
  const longPicked = <C extends { id: string; split: EvalSplit | null }>(list: readonly C[]) =>
    suite === 'long' ? list.filter((c) => picks(c.id) && inSplit(c)) : [];
  const questions = longPicked(cases.questions).filter((c) => runs(c.id));
  const conditions = longPicked(cases.conditions).filter((c) => runs(c.id));
  const ids = [
    ...cases.short.map((c) => c.id),
    ...(suite === 'long' ? [...cases.questions, ...cases.conditions].map((c) => c.id) : []),
  ];
  return {
    short: short.length,
    local: short.filter((c) => c.local).length,
    questions: questions.length,
    conditions: conditions.length,
    demo: [...cases.short.filter((c) => picks(c.id)), ...longPicked(cases.questions)].filter((c) =>
      demoIds.has(c.id)
    ).length,
    unmatched: prefixes.filter((p) => !ids.some((id) => id.startsWith(p))),
  };
}

/**
 * The METS demo questions a run could select (TOM-196): the board requests
 * on either suite, and the owner questions too on a long run over "all", the
 * only split that selects them (they are in neither train nor test).
 */
export function demoQuestionCount(
  cases: EvalLaunchOptions['cases'],
  suite: EvalSuite,
  split: EvalSplitFilter = 'all'
): number {
  return caseFilterCoverage(cases, suite, [], { includeDemo: true, split }).demo;
}

/**
 * What a selection does with the METS demo questions it picks, as a sentence
 * (TOM-196): "Leaves out 9 METS demo questions." or "Includes 9 METS demo
 * questions."; null when it picks none.
 */
export function demoCoverageText(demo: number, included: boolean): string | null {
  if (demo === 0) return null;
  const noun = demo === 1 ? 'METS demo question' : 'METS demo questions';
  return `${included ? 'Includes' : 'Leaves out'} ${demo} ${noun}.`;
}

/**
 * What the case filter selects of an expert's own cases in a split (TOM-533):
 * a hint beside the box. The estimate is the authority.
 */
export function expertCaseCoverage(
  expert: EvalLaunchExpert,
  split: EvalSplitFilter,
  prefixes: readonly string[]
): { cases: number; train: number; test: number; unmatched: string[] } {
  const picks = (id: string) => prefixes.length === 0 || prefixes.some((p) => id.startsWith(p));
  const picked = expert.cases.filter((c) => picks(c.id) && (split === 'all' || c.split === split));
  return {
    cases: picked.length,
    train: picked.filter((c) => c.split === 'train').length,
    test: picked.filter((c) => c.split === 'test').length,
    unmatched: prefixes.filter((p) => !expert.cases.some((c) => c.id.startsWith(p))),
  };
}

/** "6 of Electrical (Sparky)'s own questions (4 train, 2 held-out test)". */
export function expertCoverageText(
  expert: EvalLaunchExpert,
  c: ReturnType<typeof expertCaseCoverage>
): string {
  const parts = [
    c.train > 0 ? `${c.train} train` : null,
    c.test > 0 ? `${c.test} held-out test` : null,
  ].filter((x): x is string => x !== null);
  const noun = c.cases === 1 ? 'question' : 'questions';
  const detail = parts.length > 0 ? ` (${parts.join(', ')})` : '';
  return `${c.cases} of ${agentLabel(expert.id)}'s own ${noun}${detail}`;
}

/** "12 short cases (4 answered locally), 3 owner questions, 2 conditions". */
export function coverageText(c: ReturnType<typeof caseFilterCoverage>, suite: EvalSuite): string {
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const parts = [
    `${plural(c.short, 'short case', 'short cases')}${c.local > 0 ? ` (${c.local} answered locally, no model call)` : ''}`,
  ];
  if (suite === 'long') {
    parts.push(plural(c.questions, 'owner question', 'owner questions'));
    parts.push(plural(c.conditions, 'Watch condition', 'Watch conditions'));
  }
  return parts.join(', ');
}

const MODEL_SOURCE_TEXT: Record<EvalLaunchModelSource, string> = {
  NEO_MODEL: 'Production chat (NEO_MODEL)',
  WATCH_REVIEW_MODEL: 'Production Watch (WATCH_REVIEW_MODEL)',
  model_choice: '/model choice',
};

export function modelSourceText(source: EvalLaunchModelSource): string {
  return MODEL_SOURCE_TEXT[source];
}

// ----- Refusals in plain words --------------------------------------------------------------

const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
const numberOf = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;
const listText = (items: readonly string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

/**
 * What refuses a launch, as the page says it: a short title and what to do.
 * `what` 'regrade': the same refusal of a run's re-grade (TOM-257), which has
 * no case filter or split of its own to change.
 */
export function refusalText(
  r: EvalLaunchRefusal,
  what: 'launch' | 'regrade' = 'launch'
): { title: string; body: string } {
  if (what === 'regrade') {
    const regrade = regradeRefusalText(r);
    if (regrade !== null) return regrade;
  }
  switch (r.code) {
    case 'model_not_configured': {
      const models = strings(r.details.models);
      const them = models.length === 1 ? 'it' : 'them';
      return {
        title: 'No provider key',
        body:
          models.length > 0
            ? `No provider key is set for ${listText(models)}. A launch never skips a model, so it is refused: remove ${them}, or set the key.`
            : r.message,
      };
    }
    case 'test_split_unconfirmed': {
      const cases = strings(r.details.cases);
      const heldOut = numberOf(r.details.heldOut);
      if (cases.length > 0) {
        return {
          title: 'The case filter names held-out test cases',
          body: `The case filter names ${cases.length} held-out test ${cases.length === 1 ? 'case' : 'cases'}: ${cases.join(', ')}. Over a few named test cases, the run's figures are their verdicts. Confirm the test split below, or narrow the filter to train cases.`,
        };
      }
      if (heldOut !== null) {
        return {
          title: 'Split "all" runs the held-out test cases',
          body: `Split "all" runs the ${heldOut} held-out test ${heldOut === 1 ? 'case' : 'cases'} beside the train cases. Every test measurement is deliberate: confirm the test split below, or launch the train split.`,
        };
      }
      return {
        title: 'The test split is held out',
        body: 'The test split is held out, so nobody tunes Neo to it. Confirm it below to run it.',
      };
    }
    case 'visibility_blocked': {
      const cases = Array.isArray(r.details.cases)
        ? r.details.cases.flatMap((c) => {
            const o = c as { caseId?: unknown; split?: unknown };
            return typeof o.caseId === 'string'
              ? [typeof o.split === 'string' ? `${o.caseId} (${o.split})` : o.caseId]
              : [];
          })
        : [];
      return {
        title: 'Cases that cannot see their evidence',
        body:
          cases.length > 0
            ? `${cases.length} long ${cases.length === 1 ? 'case is' : 'cases are'} blocked: the moment does not serve the evidence the case needs, and the run store cannot record that yet: ${cases.join(', ')}. Leave ${cases.length === 1 ? 'it' : 'them'} out with the case filter.`
            : r.message,
      };
    }
    case 'daily_cap_reached': {
      const cap = numberOf(r.details.capUsd);
      const spent = numberOf(r.details.spentUsd);
      const reserved = numberOf(r.details.reservedUsd) ?? 0;
      const left = numberOf(r.details.leftUsd);
      const ceiling = numberOf(r.details.spendCeilingUsd);
      if (cap === null || spent === null || left === null || ceiling === null) {
        return { title: 'The daily eval cap', body: r.message };
      }
      if (spent >= cap) {
        return {
          title: 'The daily eval cap is spent',
          body: `Evals have spent ${formatUsd(spent)} of the ${formatUsd(cap)} daily cap in the last 24 hours. Launch when the day's spend has rolled off.`,
        };
      }
      return {
        title: 'Over the daily eval cap',
        body: `This run could spend up to ${formatUsd(ceiling)}, more than the ${formatUsd(left)} left under the ${formatUsd(cap)} daily cap${reserved > 0 ? `, counting ${formatUsd(reserved)} the runs still going may yet spend` : ''}. Lower the budget or the selection, or launch later.`,
      };
    }
    default:
      return { title: 'Refused', body: (r as { message: string }).message };
  }
}

/**
 * A refusal of a re-grade (TOM-257), where its words differ from a launch's:
 * a re-grade grades the run's stored replies again, so it has no case filter,
 * split or model to change. Null where the launch's words fit.
 */
function regradeRefusalText(r: EvalLaunchRefusal): { title: string; body: string } | null {
  switch (r.code) {
    case 'test_split_unconfirmed': {
      const n = numberOf(r.details.heldOut);
      const cases =
        n === null ? 'held-out test cases' : `${n} held-out test ${n === 1 ? 'case' : 'cases'}`;
      return {
        title: 'This run holds held-out test cases',
        body: `This run holds ${cases}, and a re-grade measures them again. Confirm the test split below to re-grade them.`,
      };
    }
    case 'visibility_blocked': {
      const cases = Array.isArray(r.details.cases)
        ? r.details.cases.flatMap((c) => {
            const id = (c as { caseId?: unknown }).caseId;
            return typeof id === 'string' ? [id] : [];
          })
        : [];
      return {
        title: 'Cases that cannot see their evidence',
        body:
          cases.length > 0
            ? `${cases.length} long ${cases.length === 1 ? 'case is' : 'cases are'} blocked: the recorded moment no longer serves the evidence the case needs, and the run store cannot record that yet (${listText(cases)}). This run cannot be re-graded until ${cases.length === 1 ? 'it is' : 'they are'} fixed.`
            : r.message,
      };
    }
    case 'daily_cap_reached': {
      const cap = numberOf(r.details.capUsd);
      const spent = numberOf(r.details.spentUsd);
      const reserved = numberOf(r.details.reservedUsd) ?? 0;
      const left = numberOf(r.details.leftUsd);
      const ceiling = numberOf(r.details.spendCeilingUsd);
      if (cap === null || spent === null || left === null || ceiling === null) return null;
      if (spent >= cap) {
        return {
          title: 'The daily eval cap is spent',
          body: `Evals have spent ${formatUsd(spent)} of the ${formatUsd(cap)} daily cap in the last 24 hours. Re-grade when the day's spend has rolled off.`,
        };
      }
      return {
        title: 'Over the daily eval cap',
        body: `This re-grade could spend up to ${formatUsd(ceiling)}, more than the ${formatUsd(left)} left under the ${formatUsd(cap)} daily cap${reserved > 0 ? `, counting ${formatUsd(reserved)} the runs still going may yet spend` : ''}. Lower the budget, or re-grade later.`,
      };
    }
    default:
      return null;
  }
}

const REFUSAL_CODES: readonly string[] = [
  'model_not_configured',
  'test_split_unconfirmed',
  'visibility_blocked',
  'daily_cap_reached',
];

/** Why a launch or an estimate request failed, from the error body the route answered. */
export function launchErrorText(
  body: {
    error?: string;
    message?: string;
    details?: Record<string, unknown>;
  },
  what: 'launch' | 'regrade' = 'launch'
): string {
  const code = body.error ?? '';
  const message = typeof body.message === 'string' ? body.message : '';
  if (REFUSAL_CODES.includes(code)) {
    const { title, body: text } = refusalText(
      {
        code: code as EvalLaunchRefusalCode,
        message,
        details: body.details ?? {},
      },
      what
    );
    return `Refused (${title.toLowerCase()}): ${text}`;
  }
  switch (code) {
    case 'unknown_model':
      return `${message.length > 0 ? message : 'A model id is not one the eval can run'}. A model id names its vendor, such as openrouter:deepseek/deepseek-v4-pro.`;
    case 'no_cases': {
      // A filter that picks only METS demo questions, left out (TOM-196).
      const demo = numberOf(body.details?.demoLeftOut);
      return demo !== null && demo > 0
        ? `The case filter picks only METS demo questions (${demo}), and they are left out: include the METS demo questions, or widen the filter.`
        : 'The selection holds no case for any model: widen the case filter.';
    }
    case 'invalid_launch':
      return `The launch is not valid: ${message}.`;
    case 'audit_unavailable':
      return 'The launch could not be audited, so nothing was launched.';
    default:
      return message.length > 0 ? message : 'The request failed.';
  }
}

/** The test split's refusal in an estimate, when it lists one. */
export function testSplitRefusal(estimate: EvalLaunchEstimate | null): EvalLaunchRefusal | null {
  return estimate?.refusals.find((r) => r.code === 'test_split_unconfirmed') ?? null;
}

/**
 * How many held-out test cases a launch runs, as far as the estimate says:
 * the refusal's count or its named cases. Null when it does not say.
 */
export function heldOutCount(refusal: EvalLaunchRefusal | null): number | null {
  if (refusal === null) return null;
  const n = numberOf(refusal.details.heldOut);
  if (n !== null) return n;
  const cases = strings(refusal.details.cases);
  return cases.length > 0 ? cases.length : null;
}

/** Where an estimate figure came from (cloud estimate.ts). */
export type EvalEstimateBasis = 'history' | 'measured' | 'default';

/**
 * Where a per-turn figure came from: the run it was read from, the measured
 * table (the Run D baseline, TOM-120), or the default.
 */
export function basisText(basis: EvalEstimateBasis, runId: string | null): string {
  if (basis === 'history') return runId !== null ? `from run ${runId.slice(0, 8)}` : 'from history';
  if (basis === 'measured') return 'measured (no stored run here)';
  return 'default (no stored run)';
}

const DETECTABLE_RATE_TEXT: Record<EvalDetectableRate, string> = {
  attacks: 'attacks resisted',
  helpful: 'helpful answered',
  questions: 'questions met',
  conditions: 'conditions met',
};

/**
 * The smallest gain each compared rate of an estimate row could show (TOM-537),
 * e.g. "questions met on train +33 pts (12 cases) · questions met on test none
 * (2 cases)". Whole points rounded up, so a gain never reads smaller than it
 * is. Null when the row has none (a re-grade, or no case reaches the model).
 */
export function detectableText(detectable: readonly EvalDetectableEffect[]): string | null {
  if (detectable.length === 0) return null;
  return detectable
    .map((d) => {
      const where = d.split === 'all' ? '' : ` on ${d.split}`;
      const gain = d.effect >= 1 ? 'none' : `+${Math.ceil(d.effect * 100 - 1e-9)} pts`;
      const cases = `${d.cases} ${d.cases === 1 ? 'case' : 'cases'}`;
      return `${DETECTABLE_RATE_TEXT[d.rate]}${where} ${gain} (${cases})`;
    })
    .join(' · ');
}

/** What the detectable gain means, for the line's title. */
export const DETECTABLE_TITLE =
  'The smallest gain in each rate that comparing two runs like this one reads as a gain 80% of the time, from a 50% rate: the paired interval must see it (McNemar’s test reads only a fall). It assumes each case at a recorded moment of its own: long cases that share a moment need a larger gain. "none": no gain is seen that often. Informational: it refuses nothing.';
